import React from "react";
import PropTypes from "prop-types";
import {
  TextField,
  MenuItem,
  Box,
  Chip,
  Typography,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  createFilterOptions,
} from "@mui/material";
import isUndefined from "lodash/isUndefined";
import get from "lodash/get";
import DatePickerField from "components/DateComponets/DatePicker";
import dayjs from "dayjs";

export const FormInputField = ({
  field,
  value,
  formik,
  className = "",
  renderOptions = null,
  ...rest
}) => {
  if (
    field?.shouldHide &&
    field.shouldHide({ field, value, formik, renderOptions, ...rest })
  ) {
    return null;
  }
  if (field.type === "dateField") {
    return <FormDateField field={field} formik={formik} {...rest} />;
  }
  if (field.creatable) {
    return (
      <FormCreatableField
        field={field}
        formik={formik}
        options={field.options}
        {...rest}
      />
    );
  }
  if (field.dynamicField) {
    return <FormAutoCompleteField field={field} formik={formik} {...rest} />;
  }
  return (
    <div
      className={`form-field-wrapper ${field.className}`}
      style={{ width: field.width }}
    >
      <FormLabel text={field.label} required={field.required} />
      <TextField
        key={field.key}
        id={field.key}
        type={field.type}
        select={field.select}
        name={field.key}
        fullWidth
        variant="outlined"
        placeholder={field.placeholder}
        className={`registration-formField ${className || field.className}`}
        value={field?.customValue ? field?.customValue(value) : value}
        disabled={field.disabled}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        // error={formik.touched[field.key] && Boolean(formik.errors[field.key])}
        // helperText={formik.touched[field.key] && formik.errors[field.key]}
        error={Boolean(formik.errors[field.key])}
        helperText={formik.errors[field.key]}
        SelectProps={{
          displayEmpty: true,
          multiple: field.multiple,
          renderValue: (selected, ...rest) => {
            if (field.multiple) {
              if (selected && selected.length === 0) {
                return <em>{field.placeholder}</em>;
              }
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    marginTop: "-0.75rem",
                  }}
                >
                  {selected &&
                    selected.length > 0 &&
                    selected.map(
                      (value) =>
                        value && (
                          <Chip
                            key={value}
                            label={value}
                            style={{ fontSize: "12px" }}
                            variant="outlined"
                            onDelete={() => {
                              formik.setFieldValue(
                                field.key,
                                selected.filter((item) => item !== value)
                              );
                            }}
                          />
                        )
                    )}
                </Box>
              );
            } else {
              return (
                <Typography>
                  {typeof field.options[0] === "string"
                    ? value
                    : field.options.find((option) => option.key === selected)
                        ?.text}
                </Typography>
              );
            }
          },
        }}
        {...rest}
      >
        <MenuItem value={field?.placeholder || null} disabled>
          {field.placeholder}
        </MenuItem>
        {renderOptions
          ? renderOptions()
          : field.options &&
            field.options.map((option) => {
              return !isUndefined(option.key) ? (
                <MenuItem
                  key={option.key}
                  value={option.key}
                  selected={formik.values[field.key] === option.key}
                >
                  {option.text}
                </MenuItem>
              ) : (
                <MenuItem value={option}>{option}</MenuItem>
              );
            })}
      </TextField>
    </div>
  );
};

export const FormDateField = ({ field, formik, extraProps }) => {
  return (
    <div
      className={`form-field-wrapper ${field.className}`}
      style={{ width: field.width }}
    >
      <FormLabel text={field.label} required={field.required} />
      <DatePickerField
        onChange={(date) => {
          if (
            !date ||
            dayjs(date).isValid() === false ||
            dayjs(date).year() === 1970
          ) {
            formik.setFieldValue(field.key, null); // Explicitly set to null
          } else {
            formik.setFieldValue(field.key, date);
          }
        }}
        onClear={() => {
          formik.setFieldValue(field.key, null);
        }}
        value={formik.values[field.key] || null}
        field={field}
        slotProps={{
          field: {
            clearable: !field.required,
            onClear: () => {
              formik.setFieldValue(field.key, null);
            },
          },
        }}
        {...extraProps}
      />
      {formik.errors[field.key] && (
        <FormHelperText
          style={{ fontSize: "12px" }}
          error
          text={formik.errors[field.key]}
        >
          {formik.errors[field.key]}
        </FormHelperText>
      )}
    </div>
  );
};

export const FormAutoCompleteField = ({
  field,
  formik,
  dynamicOptions = {},
  ...rest
}) => {
  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);

  return (
    <div
      className={`form-field-wrapper ${field.className}`}
      style={{ width: field.width }}
    >
      <FormLabel text={field.label} required={field.required} />
      <Autocomplete
        freeSolo
        onChange={(_, value) => {
          formik.setFieldValue(field.key, value?.key);
        }}
        id="companies-autocomplete"
        value={
          get(dynamicOptions, field.key, []).find(
            (option) => option.key === formik.values[field.key]
          ) || null
        }
        className="form-field"
        open={autoCompleteOpen}
        onOpen={() => setAutoCompleteOpen(true)}
        onClose={() => setAutoCompleteOpen(false)}
        isOptionEqualToValue={(option, value) => {
          return option.key === value.companyId;
        }}
        getOptionLabel={(option) => {
          return option.text;
        }}
        options={dynamicOptions[field.key]}
        renderInput={(params) => (
          <TextField
            {...params}
            className="form-field autocomplete-field"
            onChange={(event, value) => {
              const textLen = event.target.value.length;
              if (textLen >= 3) {
                console.log("Fetching the record");
              }
            }}
            error={Boolean(formik.errors[field.key])}
            helperText={formik.errors[field.key]}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {false ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            {...rest}
          />
        )}
        {...rest}
      />
    </div>
  );
};

const filter = createFilterOptions();

export const FormCreatableField = ({
  field,
  formik,
  optionKey,
  dynamicOptions = {},
  options,
  optionsLoading,
  onChange = ({ field, inputValue, formik }) => {
    formik.setFieldValue(field.key, inputValue);
  },
  optionFormatter = (value) => value.text.trim() || value.title.trim(),
  getValue = ({ field, options, formik }) => formik.values[field.key],
  getFilterOptions = ({ filtered, params, options, rest }) => {
    const { inputValue } = params;

    // Check if input matches any existing option
    const isExisting = options.some(
      (option) =>
        inputValue === option.text.trim() || inputValue === option.title
    );

    // Add the "create new" option if input is valid and doesn't exist
    if (inputValue.trim() !== "" && !isExisting) {
      filtered.push({
        inputValue: inputValue.trim(),
        text: `Add "${inputValue.trim()}"`,
      });
    }

    return filtered;
  },
  getOptionLabel = (option, _) =>
    option?.text.trim() || option?.title.trim() || "",
  checkOptionEqualsValue = (option, value, _) => {
    if (typeof value === "string") {
      return `${option.text}`.trim() === value;
    }
    return option.key === value.key;
  },
  renderOption = (option, props) => option.text || option.title,
  ...rest
}) => {
  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);

  return (
    <div
      className={`form-field-wrapper ${field.className}`}
      style={{ width: field.width }}
    >
      <FormLabel text={field.label} required={field.required} />
      <Autocomplete
        onChange={(_, value) => {
          let inputValue = "";
          // Handle different value types
          if (typeof value === "string") {
            inputValue = value;
          } else if (value && value.inputValue) {
            // Handle the "Add new" option
            inputValue = value.inputValue;
          } else if (value) {
            // Handle selected user object
            inputValue = optionFormatter(value);
          }
          onChange({ field, inputValue, formik, value });
        }}
        id="asynchronous-demo"
        value={getValue({ field, options, formik }) || ""}
        className="form-field creatable-field autocomplete-field"
        open={autoCompleteOpen}
        onOpen={() => setAutoCompleteOpen(true)}
        onClose={() => setAutoCompleteOpen(false)}
        isOptionEqualToValue={(option, value, ...rest) => {
          return checkOptionEqualsValue(option, value, rest);
        }}
        filterOptions={(options, params, ...rest) => {
          const filtered = filter(options, params);

          return getFilterOptions({ filtered, params, options, rest });
        }}
        getOptionLabel={(option, ...rest) => {
          if (typeof option === "string") {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return getOptionLabel(option, rest).trim();
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={option[optionKey] || "new"} {...optionProps}>
              {renderOption(option, props)}
            </li>
          );
        }}
        options={dynamicOptions[field.key] || options}
        loading={optionsLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            className="form-field"
            onChange={(event) => {
              const textLen = event.target.value.length;
              if (textLen >= 3) {
                // Trigger search/fetch
                console.log("Fetching the record");
              }
            }}
            error={Boolean(formik.errors[field.key])}
            helperText={formik.errors[field.key]}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {optionsLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        {...rest}
      />
    </div>
  );
};

FormCreatableField.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    required: PropTypes.bool,
    className: PropTypes.string,
    width: PropTypes.string,
  }).isRequired,
  formik: PropTypes.shape({
    values: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  extraProps: PropTypes.object,
  optionKey: PropTypes.string,
  dynamicOptions: PropTypes.object,
  options: PropTypes.array,
  optionsLoading: PropTypes.bool,
  optionFormatter: PropTypes.func,
  getValue: PropTypes.func,
  getFilterOptions: PropTypes.func,
  getOptionLabel: PropTypes.func,
  checkOptionEqualsValue: PropTypes.func,
  renderOption: PropTypes.func,
};

export const RequiredSup = () => {
  return <sup style={{ color: "red", fontSize: "1em", top: 0 }}>*</sup>;
};

export const FormLabel = ({ className, required, text }) => {
  return (
    <p className={`form-label ${className}`}>
      <label>
        {text}
        {required && <RequiredSup />}
      </label>
    </p>
  );
};

export default FormInputField;
